/**
 * Choosing what an attribute radar shows, and where each point lands.
 *
 * A radar answers one question a table cannot: what SHAPE does this brand have across the
 * attributes AI talks about — broad and even, or spiked on two and absent elsewhere. That reading
 * only survives if the axes are stable and the series are few, which is what the selection rules
 * below are for. The table stays one click away for everything the radar deliberately drops.
 */

/** One attribute axis, with each series' score on it. */
export interface RadarAxis {
  termKey: string;
  label: string;
  /** Measured 0–100 score per series id. A series absent from the map was not measured here. */
  scores: ReadonlyMap<string, number>;
}

/** One plotted brand. */
export interface RadarSeries {
  id: string;
  label: string;
  color: string;
  isOwn: boolean;
}

/**
 * An attribute as the leaderboard already knows it, before the radar decides anything.
 *
 * `scores` must carry every series the reader could ever plot — you, the competitor average, each
 * selectable rival — and NOT only the ones currently selected. The axis set is derived from this
 * map, so narrowing it to the selection is what made the axes move when the selection changed.
 */
export interface RadarCandidateAttribute {
  termKey: string;
  label: string;
  /** How much of the conversation this attribute accounts for — the ranking key. */
  share: number;
  /** Whether this attribute is an unapproved suggestion. */
  isSuggested?: boolean;
  scores: ReadonlyMap<string, number>;
}

/** Whether a map holds a usable measurement for a series. Undefined and NaN both mean "not measured". */
function hasScore(scores: ReadonlyMap<string, number>, seriesId: string): boolean {
  return Number.isFinite(scores.get(seriesId));
}

/**
 * Whether a series has anything at all to plot on the axes the radar draws.
 *
 * The condition behind offering a comparison: a series with no measurement on any drawn axis would
 * be an empty polygon, and a control that selects one promises a reading that does not exist.
 */
export function seriesHasMeasurement(axes: readonly RadarAxis[], seriesId: string): boolean {
  return axes.some((axis) => hasScore(axis.scores, seriesId));
}

/**
 * How many axes the radar draws.
 *
 * Below the minimum a radar is not a shape — three axes make a triangle whose area says more about
 * the geometry than the data. Above the maximum the labels collide and the polygon turns into a
 * circle that hides the very spikes it exists to show.
 */
export const MINIMUM_RADAR_AXES = 4;
export const MAXIMUM_RADAR_AXES = 8;

/**
 * At most You plus three rivals.
 *
 * Four polygons is where a radar still reads: the comparison palette gives three distinct
 * comparison hues before it wraps, so every series in a full set stays separable from the others
 * and from the brand primary.
 */
export const MAXIMUM_RADAR_COMPETITORS = 3;

/**
 * The customer's own series, and the default comparison.
 *
 * Ids rather than term keys, so neither can collide with a real taxonomy term.
 */
export const OWN_SERIES_ID = '__own__';
export const COMPETITOR_AVERAGE_SERIES_ID = '__competitor-average__';

/**
 * The colour for a series at a given position.
 *
 * `You` is the theme primary wherever it appears; every comparison series comes from the shared
 * comparison palette, which structurally cannot yield the primary. That separation is the point:
 * two series sharing the brand colour made the radar unreadable, and no per-surface palette should
 * ever be able to reintroduce it.
 */
export function radarSeriesColor(isOwn: boolean, index: number, ownColor: string, comparisonPalette: readonly string[]): string {
  return isOwn ? ownColor : comparisonPalette[index % comparisonPalette.length];
}

/**
 * The axes a radar should draw, or undefined when a radar is the wrong chart.
 *
 * Ranked by share, because the radar is a summary and the attributes worth summarising are the ones
 * the conversation is actually about.
 *
 * Suggestions are excluded: an unapproved term is the product's guess, and a guess that silently
 * reshapes the polygon would put an unreviewed axis on equal footing with a curated one.
 *
 * THE AXES DO NOT DEPEND ON WHICH COMPARISON IS SELECTED. A radar answers "what shape does this
 * brand have", and that reading only survives if the axes are stable — moving them when the reader
 * picks a different rival compares two shapes drawn on two different grids, which is the one thing
 * the chart may never do. So the qualifying test runs over every series the reader could ever plot,
 * not the handful currently on screen, and the result holds while comparisons are switched.
 *
 * AN AXIS QUALIFIES IF ANY SELECTABLE SERIES MEASURES IT — a union, not an intersection. Requiring
 * every series to have a measurement pruned the chart to the handful of attributes all brands
 * happened to share, which on a real project meant four axes out of dozens. Per-series gaps are
 * handled at the right granularity by the renderer: an individual VERTEX is omitted where that one
 * series was not measured, so a missing measurement costs one point rather than the whole axis.
 *
 * An attribute NOBODY measured is still omitted, never plotted at zero. A zero on a radar is a
 * point pulled to the centre, and it reads as "this brand is bad at this" rather than "nobody
 * asked" — the single most damaging thing a radar can get wrong.
 *
 * The minimum-axis question is therefore settled ONCE for the screen: a project either has enough
 * measured attributes to draw a shape or it does not. It can no longer be true of one comparison
 * and false of the next, which previously made the whole chart appear and vanish under the reader.
 */
export function selectRadarAxes(
  candidates: readonly RadarCandidateAttribute[],
  options?: { includeSuggested?: boolean }
): RadarAxis[] | undefined {
  const usable = candidates
    .filter((candidate) => options?.includeSuggested || !candidate.isSuggested)
    // Union over the whole selectable population: one measured series anywhere in the map is enough
    // to make the attribute worth an axis, and no subset of it may narrow the set.
    .filter((candidate) => Array.from(candidate.scores.keys()).some((id) => hasScore(candidate.scores, id)))
    .slice()
    .sort((left, right) => right.share - left.share || left.label.localeCompare(right.label));

  if (usable.length < MINIMUM_RADAR_AXES) {
    return undefined;
  }

  // As many axes as the labels can carry. The preferred count is a floor for "enough to read as a
  // shape", not a ceiling — a project with a dozen scored attributes should see eight of them.
  return usable.slice(0, MAXIMUM_RADAR_AXES).map((candidate) => ({
    termKey: candidate.termKey,
    label: candidate.label,
    scores: candidate.scores,
  }));
}

/**
 * The series to plot: the customer first, then the strongest rivals, capped.
 *
 * "You" is never dropped to make room — it is the subject of every question this chart answers.
 */
export function selectRadarSeries(own: RadarSeries | undefined, competitors: readonly RadarSeries[]): RadarSeries[] {
  const rivals = competitors.slice(0, MAXIMUM_RADAR_COMPETITORS);
  return own ? [own, ...rivals] : rivals;
}

/** A point on the radar, in unit coordinates around a centre of (0, 0). */
export interface RadarPoint {
  x: number;
  y: number;
}

/**
 * Where an axis's vertex sits at a given score.
 *
 * Angles start at twelve o'clock and run clockwise, so the first (highest-share) axis is the one
 * the eye lands on. `radius` is the drawn radius at 100; a score scales linearly along it, because
 * a radar's readability rests on equal steps meaning equal differences.
 */
export function radarPoint(axisIndex: number, axisCount: number, percent: number, radius: number): RadarPoint {
  const angle = (axisIndex / Math.max(1, axisCount)) * Math.PI * 2 - Math.PI / 2;
  const scaled = (Math.max(0, Math.min(100, percent)) / 100) * radius;
  return { x: Math.cos(angle) * scaled, y: Math.sin(angle) * scaled };
}
